"use client"
import { useEffect } from 'react';
import { fieldPlaceholder } from '@/lib/form-placeholders';
import { dropdownIconFor, dropdownIconMask } from '@/components/ui/dropdown-icons';

function labelFor(field: HTMLInputElement | HTMLTextAreaElement) {
  const explicit = field.getAttribute('aria-label'); if (explicit) return explicit;
  const ids = field.getAttribute('aria-labelledby');
  if (ids) return ids.split(/\s+/).map(id => document.getElementById(id)?.textContent || '').join(' ');
  const label = field.labels?.[0];
  if (!label) return field.name || field.id;
  const clone = label.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('input,textarea,select,button,svg,[role="status"]').forEach(node => node.remove());
  return clone.textContent?.trim() || field.name || field.id;
}
function improve(root: ParentNode) {
  root.querySelectorAll<HTMLSelectElement>('select:not([multiple]):not([data-field-unstyled]):not([aria-hidden="true"]):not([class^="ql-"]):not([class*=" ql-"])').forEach(field => {
    if (field.size > 1 || field.dataset.slot === 'native-select') return;
    if (/per page|rows|^size$/i.test(field.getAttribute('aria-label') || '') || field.closest('[data-no-dropdown-icons]')) return;
    // Legacy controls with a real leading SVG already have a context icon.
    const leadingIcon = field.parentElement?.querySelector(':scope > svg:not([data-native-select-chevron])');
    if (leadingIcon) return;
    const label = field.getAttribute('aria-label') || field.labels?.[0]?.textContent?.replace(field.textContent || '', '') || field.name || field.id;
    const icon = dropdownIconFor(label);
    if (field.dataset.dropdownIcon === icon) return;
    field.dataset.dropdownIcon = icon;
    field.style.setProperty('--dropdown-icon-mask', dropdownIconMask(icon));
    field.style.setProperty('--dropdown-padding-start', '36px');
  });
  const fields = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input,textarea');
  fields.forEach(field => {
    if (field.readOnly || field.getAttribute('placeholder')?.trim() || field.closest('[data-no-field-hints]')) return;
    const hint = fieldPlaceholder({ label: labelFor(field), name: field.name, id: field.id, type: field instanceof HTMLInputElement ? field.type : 'text', inputMode: field.inputMode, autoComplete: field.autocomplete, textarea: field instanceof HTMLTextAreaElement });
    if (hint) field.setAttribute('placeholder', hint);
  });
}
export function FormPolish() {
  useEffect(() => {
    improve(document);
    let frame: number | undefined;
    const queued = new Set<ParentNode>();
    const observer = new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'attributes' && record.target instanceof HTMLElement) queued.add(record.target.parentElement || record.target);
        for (const added of record.addedNodes) if (added instanceof HTMLElement) queued.add(added.parentElement || added);
      }
      if (frame !== undefined) return;
      frame = requestAnimationFrame(() => { frame = undefined; for (const root of queued) improve(root); queued.clear() });
    });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['id', 'name', 'aria-label', 'type', 'placeholder', 'size', 'multiple'] });
    return () => { observer.disconnect(); if (frame !== undefined) cancelAnimationFrame(frame) };
  }, []);
  return null;
}
